import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from '../components/Layout';
import { fetchPageViews, fetchPopularPages, fetchTopCountries, fetchDevices, fetchSummary } from '../services/analyticsService';
import { getCachedAnalyticsData, setCachedAnalyticsData } from '../services/analyticsCache';
import './Analytics.css';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('7');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageViewsData, setPageViewsData] = useState([]);
  const [popularPagesData, setPopularPagesData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [devicesData, setDevicesData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalViews: 0,
    uniqueUsers: 0,
    avgDailyViews: 0,
    peakDay: { date: 'N/A', views: 0 },
  });
  const [useRealData, setUseRealData] = useState(false);
  const [isCached, setIsCached] = useState(false);

  const loadFakeData = useCallback(() => {
    const fakePageViews = {
      '7': [
        { date: 'Jan 6', views: 245 },
        { date: 'Jan 7', views: 312 },
        { date: 'Jan 8', views: 398 },
        { date: 'Jan 9', views: 445 },
        { date: 'Jan 10', views: 521 },
        { date: 'Jan 11', views: 478 },
        { date: 'Jan 12', views: 448 },
      ],
      '30': [
        { date: 'Dec 13', views: 380 },
        { date: 'Dec 16', views: 425 },
        { date: 'Dec 19', views: 390 },
        { date: 'Dec 22', views: 445 },
        { date: 'Dec 25', views: 520 },
        { date: 'Dec 28', views: 485 },
        { date: 'Dec 31', views: 510 },
        { date: 'Jan 3', views: 395 },
        { date: 'Jan 6', views: 430 },
        { date: 'Jan 9', views: 465 },
        { date: 'Jan 12', views: 448 },
      ],
      '90': [
        { date: 'Oct 12', views: 320 },
        { date: 'Oct 26', views: 355 },
        { date: 'Nov 9', views: 390 },
        { date: 'Nov 23', views: 425 },
        { date: 'Dec 7', views: 460 },
        { date: 'Dec 21', views: 495 },
        { date: 'Jan 4', views: 410 },
        { date: 'Jan 12', views: 448 },
      ],
    };
    const fakePopularPages = [
      { page: '/vendrs', views: 1247, percentage: 32 },
      { page: '/', views: 1089, percentage: 28 },
      { page: '/about', views: 876, percentage: 22 },
      { page: '/contact', views: 635, percentage: 18 },
    ];
    const fakeCountries = [
      { name: 'United Kingdom', visitors: 1523, color: '#ffd700' },
      { name: 'United States', visitors: 847, color: '#4a90e2' },
      { name: 'Germany', visitors: 432, color: '#50c878' },
      { name: 'France', visitors: 298, color: '#9370db' },
      { name: 'Other', visitors: 543, color: '#e0e0e0' },
    ];
    const fakeDevices = [
      { name: 'Desktop', value: 55, color: '#ffd700' },
      { name: 'Mobile', value: 35, color: '#4a90e2' },
      { name: 'Tablet', value: 10, color: '#50c878' },
    ];
    setPageViewsData(fakePageViews[dateRange] || fakePageViews['7']);
    setPopularPagesData(fakePopularPages);
    setCountriesData(fakeCountries);
    setDevicesData(fakeDevices);
    setSummaryData({
      totalViews: fakePageViews[dateRange]?.reduce((sum, d) => sum + d.views, 0) || 2847,
      uniqueUsers: 1523,
      avgDailyViews: Math.round(2847 / parseInt(dateRange)),
      peakDay: { date: 'Jan 10', views: 521 },
    });
  }, [dateRange]);

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const days = parseInt(dateRange);
    
    // Check shared cache first
    const cached = getCachedAnalyticsData(days);
    if (cached) {
      console.log('Using cached data for', days, 'days');
      setPageViewsData(cached.pageViews || []);
      setPopularPagesData(cached.popularPages || []);
      setCountriesData(cached.countries || []);
      setDevicesData(cached.devices || []);
      setSummaryData(cached.summary || {
        totalViews: 0,
        uniqueUsers: 0,
        avgDailyViews: 0,
        peakDay: { date: 'N/A', views: 0 },
      });
      setUseRealData(true);
      setIsCached(true);
      setLoading(false);
      return;
    }

    setIsCached(false);

    try {
      // Try to fetch real data
      const [pageViews, popularPages, countries, devices, summary] = await Promise.all([
        fetchPageViews(days),
        fetchPopularPages(days),
        fetchTopCountries(days),
        fetchDevices(days),
        fetchSummary(days),
      ]);

      setPageViewsData(pageViews);
      
      // Format popular pages with percentages
      const totalPageViews = popularPages.reduce((sum, p) => sum + p.views, 0);
      const formattedPages = popularPages.map((page) => ({
        ...page,
        percentage: totalPageViews > 0 ? Math.round((page.views / totalPageViews) * 100) : 0,
      }));
      setPopularPagesData(formattedPages);

      setCountriesData(countries);
      setDevicesData(devices);
      setSummaryData(summary);
      setUseRealData(true);
      
      // Cache the results in shared cache
      setCachedAnalyticsData(days, {
        pageViews,
        popularPages: formattedPages,
        countries,
        devices,
        summary,
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
      // Fall back to fake data if API fails
      loadFakeData();
      setUseRealData(false);
    } finally {
      setLoading(false);
    }
  }, [dateRange, loadFakeData]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const getTotalViews = () => {
    return summaryData.totalViews || pageViewsData.reduce((sum, item) => sum + item.views, 0);
  };

  const getAvgDailyViews = () => {
    return summaryData.avgDailyViews || Math.round(getTotalViews() / parseInt(dateRange));
  };

  if (loading) {
    return (
      <Layout>
        <div className="analytics">
          <div className="analytics-header">
            <h1>Analytics Dashboard</h1>
            <p>Loading analytics data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="analytics">
        <div className="analytics-header">
          <h1>Analytics Dashboard</h1>
          <p>Website traffic and visitor insights</p>
          {error && (
            <div className="analytics-error">
              <p><strong>⚠️ Error:</strong> {error}</p>
              <p>Showing sample data. Check Edge Functions setup and Supabase secrets.</p>
              <details style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                <summary style={{ cursor: 'pointer', color: '#666' }}>Troubleshooting</summary>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', color: '#666' }}>
                  <li>Verify secrets are set in Supabase Dashboard → Project Settings → Edge Functions → Secrets</li>
                  <li>Check Edge Function logs in Supabase Dashboard → Edge Functions → Logs</li>
                  <li>Ensure service account has access to GA4 property</li>
                </ul>
              </details>
            </div>
          )}
          {useRealData && (
            <div className="analytics-success">
              <p>
                ✅ Connected to Google Analytics 
                {isCached && <span style={{ color: '#666', fontSize: '0.9em' }}> (cached - no API call)</span>}
              </p>
              {!isCached && (
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
                  Data cached for 5 minutes to optimize API usage
                </p>
              )}
            </div>
          )}
        </div>

        {/* Date Range Selector */}
        <div className="date-range-selector">
          <label>Time Period:</label>
          <div className="range-buttons">
            <button 
              className={`range-btn ${dateRange === '7' ? 'active' : ''}`}
              onClick={() => setDateRange('7')}
            >
              Last 7 Days
            </button>
            <button 
              className={`range-btn ${dateRange === '30' ? 'active' : ''}`}
              onClick={() => setDateRange('30')}
            >
              Last 30 Days
            </button>
            <button 
              className={`range-btn ${dateRange === '90' ? 'active' : ''}`}
              onClick={() => setDateRange('90')}
            >
              Last 90 Days
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="analytics-summary">
          <div className="summary-card">
            <h3>Total Views</h3>
            <p className="summary-value">{getTotalViews().toLocaleString()}</p>
            <span className="summary-period">Last {dateRange} days</span>
          </div>
          <div className="summary-card">
            <h3>Avg. Daily Views</h3>
            <p className="summary-value">{getAvgDailyViews().toLocaleString()}</p>
            <span className="summary-period">Last {dateRange} days</span>
          </div>
          <div className="summary-card">
            <h3>Peak Day</h3>
            <p className="summary-value">
              {summaryData.peakDay.views}
            </p>
            <span className="summary-period">
              {summaryData.peakDay.date}
            </span>
          </div>
        </div>

        {/* Page Views Chart */}
        <div className="analytics-chart">
          <h2>Page Views Over Time</h2>
          {pageViewsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={pageViewsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="#ffd700" strokeWidth={3} name="Page Views" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p>No data available</p>
          )}
        </div>

        {/* Two Column Layout */}
        <div className="analytics-grid">
          {/* Most Popular Pages */}
          <div className="analytics-card">
            <h2>Popular Pages</h2>
            <p className="analytics-card-note">Only real site pages are shown. Bots, typos, and translation URLs are excluded.</p>
            <div className="pages-list">
              {popularPagesData.map((page, index) => (
                <div key={index} className="page-item">
                  <div className="page-info-row">
                    <span className="page-rank">#{index + 1}</span>
                    <span className="page-path">{page.page}</span>
                    <span className="page-count">{page.views.toLocaleString()} views</span>
                  </div>
                  <div className="page-bar">
                    <div className="page-bar-fill" style={{ width: `${page.percentage}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Countries */}
          <div className="analytics-card">
            <h2>Top Countries</h2>
            <div className="countries-chart">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={countriesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="visitors"
                  >
                    {countriesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="countries-legend">
                {countriesData.map((country, index) => (
                  <div key={index} className="legend-item">
                    <div className="legend-color" style={{ background: country.color }}></div>
                    <span className="legend-name">{country.name}</span>
                    <span className="legend-value">{country.visitors.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Device Types Chart */}
        <div className="analytics-chart">
          <h2>Visitors by Device Type</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={devicesData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit="%" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="value" name="Percentage">
                {devicesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Note about data source */}
        {!useRealData && (
          <div className="analytics-note">
            <p><strong>Note:</strong> Currently showing sample data. Check Edge Functions setup to connect Google Analytics.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;

